import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import {
  ArrowLeft, Package, Clock, ShoppingCart, Hammer, Wrench, Truck, CheckCircle,
  Plus, Save, Loader, ChevronRight, User, MapPin, Calendar, DollarSign,
  FileText, Package as BoxIcon, AlertTriangle
} from 'lucide-react';
import ProjectWorkflow from './ProjectWorkflow';
import StageProgressForm from './StageProgressForm';
import toast from 'react-hot-toast';

const STAGE_CONFIG = {
  PLANNING: { label: 'Planejamento', icon: Clock, color: 'gray', bg: 'bg-gray-100' },
  PURCHASING: { label: 'Compras', icon: ShoppingCart, color: 'amber', bg: 'bg-amber-100' },
  PRODUCTION: { label: 'Produção', icon: Hammer, color: 'blue', bg: 'bg-blue-100' },
  ASSEMBLY: { label: 'Montagem', icon: Wrench, color: 'orange', bg: 'bg-orange-100' },
  COMPLETED: { label: 'Concluído', icon: CheckCircle, color: 'green', bg: 'bg-green-100' },
  DELIVERED: { label: 'Entregue', icon: Truck, color: 'purple', bg: 'bg-purple-100' }
};

const ProjectExecutionDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('workflow');

  // Buscar dados do projeto
  const { data: projectData, isLoading: loadingProject } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProjectById(projectId)
  });

  // Buscar workflow stages
  const { data: stagesData, isLoading: loadingStages } = useQuery({
    queryKey: ['project-stages', projectId],
    queryFn: () => projectsService.getProjectStages(projectId)
  });

  // Buscar progresso
  const { data: progressData, isLoading: loadingProgress } = useQuery({
    queryKey: ['project-progress', projectId],
    queryFn: () => projectsService.getProjectProgress(projectId)
  });

  const project = projectData?.data;
  const currentStage = project?.current_stage || 'PLANNING';
  const currentStageConfig = STAGE_CONFIG[currentStage];
  const StageIcon = currentStageConfig?.icon || Clock;
  const progressEntries = progressData?.data?.entries || [];
  const timeline = stagesData?.data?.timeline || [];

  // Calcular média de progresso
  const avgProgress = progressEntries.length > 0
    ? Math.round(progressEntries.reduce((sum, p) => sum + (p.progress_percentage || 0), 0) / progressEntries.length)
    : 0;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loadingProject || loadingStages) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/projetos')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{project?.project_code || `PROJ-${projectId}`}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentStageConfig?.bg} ${currentStageConfig?.color === 'gray' ? 'text-gray-700' : `text-${currentStageConfig?.color}-700`}`}>
              <StageIcon className="w-4 h-4 inline mr-1" />
              {currentStageConfig?.label || currentStage}
            </span>
          </div>
          <p className="text-gray-600">{project?.project_name || project?.client_name}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/projetos/${projectId}/orcamento`}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <FileText className="w-4 h-4" />
            Orçamento
          </Link>
          <Link
            to={`/projetos/${projectId}/horas`}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Clock className="w-4 h-4" />
            Horas
          </Link>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Cliente</p>
              <p className="font-medium text-gray-900">{project?.client_name || '-'}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-green-500">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Valor do Projeto</p>
              <p className="font-medium text-gray-900">{formatCurrency(project?.project_value)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-500">Data Início</p>
              <p className="font-medium text-gray-900">{formatDate(project?.created_at)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-3">
            <BoxIcon className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm text-gray-500">Progresso</p>
              <p className="font-medium text-gray-900">{avgProgress}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('workflow')}
          className={`px-4 py-3 font-medium ${activeTab === 'workflow' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500'}`}
        >
          Workflow
        </button>
        <button
          onClick={() => setActiveTab('progress')}
          className={`px-4 py-3 font-medium ${activeTab === 'progress' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500'}`}
        >
          Progresso ({progressEntries.length})
        </button>
        <button
          onClick={() => setActiveTab('details')}
          className={`px-4 py-3 font-medium ${activeTab === 'details' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500'}`}
        >
          Detalhes
        </button>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === 'workflow' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Workflow Timeline */}
          <div>
            <ProjectWorkflow
              projectId={projectId}
              currentStage={currentStage}
              onStageChange={() => {
                queryClient.invalidateQueries(['project', projectId]);
                queryClient.invalidateQueries(['project-stages', projectId]);
              }}
            />
          </div>

          {/* Registro de Progresso */}
          <div>
            <StageProgressForm
              projectId={projectId}
              currentStage={currentStage}
              onSuccess={() => {
                queryClient.invalidateQueries(['project-progress', projectId]);
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'progress' && (
        <div className="space-y-4">
          {progressEntries.length === 0 ? (
            <div className="card p-8 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum progresso registrado ainda.</p>
              <p className="text-sm text-gray-400">Use o formulário acima para registrar.</p>
            </div>
          ) : (
            progressEntries.map((entry, index) => (
              <div key={index} className="card p-4 flex items-start gap-4">
                <div className={`p-3 rounded-full ${STAGE_CONFIG[entry.stage]?.bg || 'bg-gray-100'}`}>
                  {React.createElement(STAGE_CONFIG[entry.stage]?.icon || CheckCircle, {
                    className: `w-5 h-5 ${STAGE_CONFIG[entry.stage] ? `text-${STAGE_CONFIG[entry.stage].color}-600` : 'text-gray-600'}`
                  })}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">{entry.stage}</h4>
                    <span className="text-sm text-gray-500">
                      {entry.progress_percentage}%
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{entry.description}</p>
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(entry.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Dados do Projeto</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Código:</span>
                <span className="font-medium">{project?.project_code || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Nome:</span>
                <span className="font-medium">{project?.project_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cliente:</span>
                <span className="font-medium">{project?.client_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status:</span>
                <span className="font-medium">{project?.status || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Etapa:</span>
                <span className="font-medium">{currentStageConfig?.label || currentStage}</span>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Valores</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Valor do Projeto:</span>
                <span className="font-medium">{formatCurrency(project?.project_value)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Criado em:</span>
                <span className="font-medium">{formatDate(project?.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Última Atualização:</span>
                <span className="font-medium">{formatDate(project?.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectExecutionDetail;